import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTrellisFocusRing } from '../../hooks';
import { generateTextTruncateStyles } from '../Text/Text.helpers';
import { getColorTokensForVariant, getIsSafeUrl } from './Link.helpers';
import { mergeProps } from 'react-aria';
import { ExternalLinkIcon } from 'foundations-assets';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledLink = styled.a.withConfig({
  displayName: "Link__StyledLink",
  componentId: "sc-1vekr46-0"
})(({
  $variant,
  $alwaysUnderline,
  $truncate,
  $focusRingStyles,
  $display,
  href,
  theme: _theme
}) => {
  const theme = _theme;
  const variantColorTokens = getColorTokensForVariant($variant);
  return Object.assign({}, $focusRingStyles, {
    display: $display
  }, $truncate ? generateTextTruncateStyles($truncate) : {}, {
    // Needs to come after the `display` property so that the truncation styles can override the `display` property.
    textDecoration: $alwaysUnderline ? 'underline' : 'none',
    borderRadius: theme.borderRadius['default'],
    // The border radius is always applied so that there aren't any border radius changes when the focus ring appears

    /*
     * Standalone links need this font weight, which is different than the font weight set in global styles.
     * Since links can also be children of text-based components, there may be font weight inconsistencies when used in text that doesn't have the same font weight.
     * This is a known issue, and is also an issue that exists in UILink.
     * UX is aware of the trade-off and we will re-consider this decision as part of a future design refresh.
     */
    fontWeight: theme.fontWeight['display-default']
  }, href === undefined ? {
    color: theme.color[variantColorTokens.disabled],
    cursor: 'not-allowed'
  } : {
    color: theme.color[variantColorTokens.idle],
    cursor: 'pointer',
    '&:hover': {
      color: theme.color[variantColorTokens.hover],
      textDecoration: 'underline'
    },
    '&:active': {
      color: theme.color[variantColorTokens.active],
      textDecoration: 'underline'
    }
  });
});
const StyledLinkIconContainer = styled.span.withConfig({
  displayName: "Link__StyledLinkIconContainer",
  componentId: "sc-1vekr46-1"
})(({
  $location,
  theme: _theme
}) => {
  const theme = _theme;
  const paddingStyle = $location === 'start' ? {
    paddingInlineEnd: theme.space['action-gap-default']
  } : {
    paddingInlineStart: theme.space['action-gap-external-icon']
  };
  return Object.assign({}, paddingStyle, {
    verticalAlign: 'middle'
  });
});
export const Link = ({
  variant = 'action',
  alwaysUnderline = false,
  truncate = false,
  display = truncate === false ? 'inline' : undefined,
  // If `truncate` is set to anything other than `false`, the display property is set using the truncate styles.
  icon: Icon,
  id,
  href: hrefProp,
  target,
  rel = target === '_blank' ? 'noopener noreferrer' : undefined,
  referrerPolicy,
  download,
  ping,
  onClick,
  onKeyDown,
  children
}) => {
  const {
    focusRingStyles,
    focusProps
  } = useTrellisFocusRing();
  const linkElementRef = useRef(null);
  const href = getIsSafeUrl(hrefProp) ? hrefProp : undefined;
  const isPlaceholder = href === undefined;

  /*
   * Adds the `ping` attribute to the `a` element when the `ping` prop changes.
   * This fixes the bug reported in https://git.hubteam.com/HubSpot/foundations-components/issues/677
   */
  useEffect(() => {
    if (ping !== undefined) {
      var _linkElementRef$curre;
      (_linkElementRef$curre = linkElementRef.current) === null || _linkElementRef$curre === void 0 || _linkElementRef$curre.setAttribute('ping', ping);
    }
  }, [ping]);
  const handleClick = e => {
    if (isPlaceholder) {
      e.stopPropagation(); // Prevents the click event from bubbling up when the link is a placeholder
      e.preventDefault(); // Prevents the link from navigating on click when the link is a placeholder
    } else if (typeof onClick === 'function') {
      onClick(e);
    }
  };
  const handleKeyDown = e => {
    if (isPlaceholder) {
      // We don't want to `preventDefault` because it would prevent keyboard and screen reader navigation

      e.stopPropagation(); // Prevents the keydown event from bubbling up when the link is a placeholder
    } else if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  };

  // Ensures that `handleClick` and `handleKeyDown` are called after `focusProps` `onClick` and `onKeyDown` handlers (if they exist)
  const mergedFocusProps = mergeProps(focusProps, {
    onClick: handleClick,
    onKeyDown: handleKeyDown
  });
  return /*#__PURE__*/_jsxs(StyledLink, Object.assign({}, mergedFocusProps, {
    id: id,
    href: href,
    target: target,
    rel: rel,
    referrerPolicy: referrerPolicy,
    download: download,
    ping: ping,
    tabIndex: 0 // Ensures that the link is focusable even if an `href` is not provided
    ,
    $display: display,
    $variant: variant,
    $alwaysUnderline: alwaysUnderline,
    $truncate: truncate,
    $focusRingStyles: focusRingStyles,
    ref: linkElementRef,
    children: [Icon !== undefined ? /*#__PURE__*/_jsx(StyledLinkIconContainer, {
      $location: "start",
      children: /*#__PURE__*/_jsx(Icon, {})
    }) : null, children, target === '_blank' ? /*#__PURE__*/_jsx(StyledLinkIconContainer, {
      $location: "end",
      children: /*#__PURE__*/_jsx(ExternalLinkIcon, {})
    }) : null]
  }));
};